class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;

    // Guard against missing input element (e.g., when Algolia search replaces the search UI)
    if (!this.input) return;

    // Guard against FoxTheme.utils not ready (e.g., Clarity overrides customElements.define)
    if (!FoxTheme.utils) return;

    // Guard against missing resetButton (e.g., when Algolia search is enabled)
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.clear.bind(this));
    }
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    // Persist search query from URL into input
    this.persistQueryFromURL();

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
      // Also hide the search content dropdown
      if (this.searchContent) {
        this.searchContent.classList.add('hidden');
      }
      // Clear the search input
      if (this.input) {
        this.input.value = '';
        this.input.blur();
      }
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('[id^="SearchProductTypes-"]');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(_evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value !== '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    // header.liquid renders two <predictive-search> instances (desktop +
    // mobile) from the same section, each with its own instance-scoped
    // results container id (predictive-search.liquid's `instance_id`) — read
    // this element's own id rather than reconstructing 'PredictiveSearchResults-'
    // + section id, which would match neither instance and/or collide between them.
    const resultsElement = this.querySelector('[id^="PredictiveSearchResults-"]');
    if (!resultsElement) return;
    const id = resultsElement.id;
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    // Close button should always close, regardless of where it is
    const isCloseButton = target.classList.contains('header__search-close') || target.closest('.header__search-close');
    const shouldClose = this.isHeaderSearch
      ? isCloseButton ||
        (!this.contains(target) &&
          target.classList.contains('fixed-overlay') && target.closest('.header-section'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton?.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton?.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }

  persistQueryFromURL() {
    // Skip on collection pages - they use ?q= for collection-specific filtering
    if (window.location.pathname.includes('/collections/')) {
      return;
    }

    // Get query from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');

    // If there's a query and the input is empty, populate it
    if (query && this.input && !this.input.value) {
      this.input.value = query;
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

// Mobile search button handler (desktop overlay)
//
// On most pages this file is lazy-appended on first search-input focus/touch
// (see theme.liquid's loadPredictiveSearch), which happens well after
// DOMContentLoaded has already fired — a plain `document.addEventListener(
// 'DOMContentLoaded', ...)` registered at that point never runs, since the
// event already happened. Run immediately if the document is already past
// the loading phase (the common case here), otherwise wait for it.
function initMobileSearchHandlers() {
  const mobileSearchButtons = document.querySelectorAll('[data-mobile-search-toggle]');
  mobileSearchButtons.forEach(button => {
    button.addEventListener('click', () => {
      const headerSearch = document.querySelector('.header__search');
      const searchInput = headerSearch?.querySelector('.search__input');
      if (searchInput) {
        // Add search-open class to body to show the search
        document.body.classList.add('search-open');
        // Focus the input after a brief delay to ensure it's visible
        setTimeout(() => {
          searchInput.focus();
        }, 100);
      }
    });
  });

  // Mobile search expand button handler (for non-homepage mobile)
  const mobileSearchExpandButtons = document.querySelectorAll('[data-mobile-search-expand]');
  mobileSearchExpandButtons.forEach(button => {
    button.addEventListener('click', () => {
      const searchContainer = document.querySelector('[data-mobile-search-container]');
      const searchInput = searchContainer?.querySelector('.search__input');

      if (searchContainer) {
        // Show the search input
        searchContainer.classList.add('is-expanded');
        // Hide the toggle button
        button.classList.add('is-hidden');
        // Focus the input
        setTimeout(() => {
          searchInput?.focus();
        }, 100);
      }
    });
  });

  // Mobile search close button handler
  const mobileSearchCloseButtons = document.querySelectorAll('[data-mobile-search-close]');
  mobileSearchCloseButtons.forEach(button => {
    button.addEventListener('click', () => {
      const searchContainer = document.querySelector('[data-mobile-search-container]');
      const searchInput = searchContainer?.querySelector('.search__input');
      const searchContent = searchContainer?.querySelector('.search__content');
      const predictiveSearch = searchContainer?.querySelector('predictive-search');
      const expandButton = document.querySelector('[data-mobile-search-expand]');

      if (searchContainer) {
        // Hide the search input on non-homepage
        searchContainer.classList.remove('is-expanded');
        // Hide the search content/dropdown
        if (searchContent) {
          searchContent.classList.add('hidden');
        }
        // Remove overlay by removing search-open class from body
        document.body.classList.remove('search-open');
        // Remove predictive-search-open class from the component
        if (predictiveSearch) {
          predictiveSearch.classList.remove('predictive-search-open');
        }
        // Clear the search input
        if (searchInput) {
          searchInput.value = '';
          searchInput.blur();
        }
        // Show the expand button again
        if (expandButton) {
          expandButton.classList.remove('is-hidden');
        }
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileSearchHandlers);
} else {
  initMobileSearchHandlers();
}
